import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface Participant {
  id: string;
  meeting_id: string;
  user_id: string;
  participant_name: string;
  joined_at: string;
  left_at: string | null;
}

interface MeetingWithParticipants {
  id: string;
  code: string;
  title: string;
  created_by: string;
  host_name: string;
  is_active: boolean;
  created_at: string;
  ended_at: string | null;
  participants: Participant[];
  participantCount: number;
}

interface MeetingStats {
  totalMeetings: number;
  activeMeetings: number;
  totalParticipants: number;
  meetingsThisMonth: number;
}

interface PermissionEntry {
  id: string;
  user_id: string;
  granted_by: string;
  granted_at: string;
  user: {
    id: string;
    email: string;
    raw_user_meta_data: { prenom?: string; nom?: string; role?: string };
  } | null;
}

type TabKey = 'meetings' | 'permissions';

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: 'meetings', label: 'Réunions', icon: 'ri-vidicon-line' },
  { key: 'permissions', label: 'Permissions', icon: 'ri-shield-keyhole-line' },
];

export default function AdminVideoMeetingsPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meetings, setMeetings] = useState<MeetingWithParticipants[]>([]);
  const [stats, setStats] = useState<MeetingStats>({ totalMeetings: 0, activeMeetings: 0, totalParticipants: 0, meetingsThisMonth: 0 });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedMeeting, setExpandedMeeting] = useState<string | null>(null);
  const [confirmEndId, setConfirmEndId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('meetings');

  // Permissions state
  const [permissions, setPermissions] = useState<PermissionEntry[]>([]);
  const [permLoading, setPermLoading] = useState(false);
  const [showPermModal, setShowPermModal] = useState(false);
  const [permEmail, setPermEmail] = useState('');
  const [permAction, setPermAction] = useState<'grant' | 'revoke'>('grant');
  const [permTargetUserId, setPermTargetUserId] = useState('');
  const [permError, setPermError] = useState('');
  const [permSuccess, setPermSuccess] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    checkAdmin();
  }, [user]);

  const checkAdmin = async () => {
    setLoading(true);
    try {
      if (role === 'super_admin' || role === 'admin' || role === 'comptable') {
        await Promise.all([loadStats(), loadMeetings()]);
        return;
      }

      const { data, error: adminError } = await supabase
        .from('super_admins')
        .select('id')
        .eq('user_id', user!.id)
        .maybeSingle();

      if (adminError || !data) {
        navigate('/', { replace: true });
        return;
      }

      await Promise.all([loadStats(), loadMeetings()]);
    } catch {
      navigate('/', { replace: true });
    }
  };

  const loadStats = async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'admin-meeting-stats' },
      });
      if (!fnError && data) setStats(data);
    } catch { /* silencieux */ }
  };

  const loadMeetings = useCallback(async () => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'admin-list-meetings', page, limit: 20, search },
      });
      if (!fnError && data) {
        setMeetings(data.meetings || []);
        setTotal(data.total || 0);
        setTotalPages(data.totalPages || 1);
      }
    } catch {
      setError('Erreur lors du chargement des réunions');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    if (user) loadMeetings();
  }, [loadMeetings, user]);

  const loadPermissions = async () => {
    setPermLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'list-permissions' },
      });
      if (!fnError && data) {
        setPermissions(data.permissions || []);
      }
    } catch { /* silencieux */ }
    setPermLoading(false);
  };

  useEffect(() => {
    if (activeTab === 'permissions' && user) {
      loadPermissions();
    }
  }, [activeTab, user]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleEndMeeting = async (code: string) => {
    try {
      const { error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'end-meeting', code },
      });
      if (!fnError) {
        setConfirmEndId(null);
        loadMeetings();
        loadStats();
      }
    } catch { /* silencieux */ }
  };

  const toggleExpand = (id: string) => {
    setExpandedMeeting(expandedMeeting === id ? null : id);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) +
      ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDuration = (start: string, end: string | null) => {
    const startD = new Date(start).getTime();
    const endD = end ? new Date(end).getTime() : Date.now();
    const diffMin = Math.round((endD - startD) / 60000);
    if (diffMin < 1) return "Moins d'1 min";
    if (diffMin < 60) return `${diffMin} min`;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  const getStatusBadge = (meeting: MeetingWithParticipants) => {
    if (meeting.is_active) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
          En cours
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
        <i className="ri-check-line text-xs"></i>
        Terminée
      </span>
    );
  };

  // --- Gestion des permissions ---
  const handleGrantPermission = async () => {
    setPermError('');
    setPermSuccess('');

    if (!permEmail.trim()) {
      setPermError("L'email est requis");
      return;
    }

    try {
      // Chercher l'utilisateur par email parmi les permissions déjà chargées
      // Utiliser l'API admin pour chercher l'utilisateur
      const { data: userList, error: listError } = await supabase.auth.admin?.listUsers
        ? await supabase.auth.admin.listUsers()
        : { data: null, error: true };

      // Alternative : via edge function
      const { data: lookupResult, error: lookupError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'list-permissions' },
      });

      if (lookupError) {
        setPermError('Erreur lors de la recherche');
        return;
      }

      // On va utiliser manage-permission directement avec l'email comme cible
      // Mais d'abord il faut trouver l'user_id
      // Solution: chercher via le edge function avec un nouvel action "find-user-by-email"
      // Pour l'instant, on utilise une approche simplifiée: on demande l'ID utilisateur

      // Approche alternative: utiliser la table medecins pour chercher par email partiel
      setPermError("Recherche d'utilisateur: utilisez l'action directement");
    } catch {
      setPermError('Erreur lors de la gestion des permissions');
    }
  };

  const handleRevokePermission = async (targetUserId: string) => {
    try {
      const { error: fnError } = await supabase.functions.invoke('manage-video-meeting', {
        body: { action: 'manage-permission', targetUserId, grant: false },
      });
      if (!fnError) {
        setPermSuccess('Permission révoquée avec succès');
        loadPermissions();
        setTimeout(() => setPermSuccess(''), 3000);
      }
    } catch {
      setPermError('Erreur lors de la révocation');
    }
  };

  const handleOpenGrantModal = () => {
    setPermError('');
    setPermSuccess('');
    setPermEmail('');
    setPermAction('grant');
    setShowPermModal(true);
  };

  const handleGrantSubmit = async () => {
    setPermError('');
    setPermSuccess('');

    if (!permEmail.trim()) {
      setPermError("L'adresse email est requise");
      return;
    }

    // Chercher l'utilisateur par email via l'edge function
    // Pour ça, on va d'abord lister les permissions et chercher manuellement...
    // Solution plus robuste : on va chercher dans les users via une nouvelle action

    // Solution simple: on fait confiance à l'admin et on laisse entrer l'email
    // On va créer une nouvelle action "grant-by-email" dans l'edge function
    // Pour l'instant, utilisons un workaround

    // Chercher l'utilisateur
    try {
      // On va chercher via un appel direct avec l'email
      const { data: lookupData, error: lookupErr } = await supabase.functions.invoke('manage-video-meeting', {
        body: {
          action: 'grant-permission-by-email',
          email: permEmail.trim(),
          grant: true,
        },
      });

      if (lookupErr || lookupData?.error) {
        setPermError(lookupData?.error || 'Utilisateur introuvable');
        return;
      }

      setPermSuccess('Permission accordée avec succès');
      loadPermissions();
      setShowPermModal(false);
      setTimeout(() => setPermSuccess(''), 3000);
    } catch {
      setPermError("Erreur lors de l'opération");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-[#00A3E0] animate-spin mb-4 block"></i>
          <div className="text-[#004976] text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FDFF] to-[#F0F7FA]">
      <Header />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <button
              onClick={() => navigate('/admin')}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1 mb-2 cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
              Retour au tableau de bord
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Visioconférences</h1>
            <p className="text-sm text-gray-500 mt-1">Historique, gestion et permissions des réunions</p>
          </div>
          <a
            href="/visioconference"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#00A3E0] text-white rounded-xl hover:bg-[#0088BD] transition-colors font-semibold text-sm whitespace-nowrap"
          >
            <i className="ri-vidicon-line"></i>
            Lancer une réunion
          </a>
        </div>

        {/* Cartes statistiques */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E0F4FC] rounded-lg flex items-center justify-center">
                <i className="ri-vidicon-line text-lg text-[#00A3E0]"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 leading-tight">{stats.totalMeetings}</div>
                <div className="text-xs text-gray-500">Total réunions</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#E8F5E9] rounded-lg flex items-center justify-center">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 leading-tight">{stats.activeMeetings}</div>
                <div className="text-xs text-gray-500">En cours</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FFF3E0] rounded-lg flex items-center justify-center">
                <i className="ri-group-line text-lg text-[#E65100]"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 leading-tight">{stats.totalParticipants}</div>
                <div className="text-xs text-gray-500">Participants (cumul)</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#EDE7F6] rounded-lg flex items-center justify-center">
                <i className="ri-calendar-line text-lg text-[#4527A0]"></i>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 leading-tight">{stats.meetingsThisMonth}</div>
                <div className="text-xs text-gray-500">Ce mois</div>
              </div>
            </div>
          </div>
        </div>

        {/* Onglets */}
        <div className="mb-6">
          <div className="inline-flex bg-gray-100 rounded-full p-1 gap-0.5">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.key
                    ? 'bg-white text-[#004976] shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <i className={`${tab.icon} text-base`}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contenu Réunions */}
        {activeTab === 'meetings' && (
          <>
            {/* Barre de recherche */}
            <form onSubmit={handleSearch} className="mb-6">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Rechercher par titre, code ou hôte..."
                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#00A3E0]/40 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  Rechercher
                </button>
                {(search || page > 1) && (
                  <button
                    type="button"
                    onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}
                    className="px-4 py-2.5 text-gray-500 hover:text-gray-700 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-close-line mr-1"></i>
                    Réinitialiser
                  </button>
                )}
              </div>
            </form>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <i className="ri-error-warning-line text-red-500"></i>
                <span className="text-sm text-red-700">{error}</span>
                <button onClick={() => { setError(null); loadMeetings(); }} className="ml-auto text-sm text-red-600 hover:text-red-800 font-medium whitespace-nowrap cursor-pointer">
                  Réessayer
                </button>
              </div>
            )}

            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {total} réunion{total !== 1 ? 's' : ''} trouvée{total !== 1 ? 's' : ''}
                {search ? ` pour "${search}"` : ''}
              </p>
            </div>

            {meetings.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-video-off-line text-2xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune réunion trouvée</h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                  {search ? 'Aucune réunion ne correspond à votre recherche.' : "Aucune réunion n'a encore été créée."}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {meetings.map((meeting) => (
                  <div
                    key={meeting.id}
                    className={`bg-white rounded-xl border transition-all duration-200 ${
                      meeting.is_active ? 'border-green-200 ring-1 ring-green-100' : 'border-gray-100'
                    }`}
                  >
                    <div
                      onClick={() => toggleExpand(meeting.id)}
                      className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
                    >
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        meeting.is_active ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        <i className={`text-lg ${meeting.is_active ? 'ri-vidicon-line text-green-600' : 'ri-check-line text-gray-500'}`}></i>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900 text-sm truncate">{meeting.title}</h3>
                          {getStatusBadge(meeting)}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
                          <span className="flex items-center gap-1"><i className="ri-user-line"></i>{meeting.host_name}</span>
                          <span className="flex items-center gap-1"><i className="ri-key-2-line"></i><code className="font-mono font-semibold">{meeting.code}</code></span>
                          <span className="flex items-center gap-1"><i className="ri-group-line"></i>{meeting.participantCount} participant{meeting.participantCount !== 1 ? 's' : ''}</span>
                          <span className="flex items-center gap-1"><i className="ri-time-line"></i>{formatDuration(meeting.created_at, meeting.ended_at)}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-shrink-0">
                        {meeting.is_active && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmEndId(meeting.code); }}
                            className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <i className="ri-stop-circle-line mr-1"></i>
                            Terminer
                          </button>
                        )}
                        <i className={`ri-arrow-down-s-line text-gray-400 transition-transform duration-200 ${
                          expandedMeeting === meeting.id ? 'rotate-180' : ''
                        }`}></i>
                      </div>
                    </div>

                    {expandedMeeting === meeting.id && (
                      <div className="border-t border-gray-100 p-4 bg-gray-50/50 rounded-b-xl">
                        <div className="flex items-center gap-2 mb-3">
                          <i className="ri-group-line text-gray-500"></i>
                          <h4 className="text-sm font-semibold text-gray-700">Participants ({meeting.participantCount})</h4>
                        </div>

                        {meeting.participants.length === 0 ? (
                          <p className="text-xs text-gray-500 py-3 text-center">Aucun participant enregistré</p>
                        ) : (
                          <div className="space-y-2">
                            {meeting.participants.map((p) => (
                              <div key={p.id} className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-gray-100">
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="w-8 h-8 bg-[#E0F4FC] rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-bold text-[#00A3E0]">{p.participant_name.charAt(0).toUpperCase()}</span>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {p.participant_name}
                                      {p.user_id === meeting.created_by && (
                                        <span className="ml-2 text-xs text-[#00A3E0] font-normal">(Hôte)</span>
                                      )}
                                    </p>
                                    <p className="text-xs text-gray-500">Rejoint le {formatDate(p.joined_at)}</p>
                                  </div>
                                </div>
                                <div className="flex-shrink-0 ml-2">
                                  {p.left_at ? (
                                    <span className="text-xs text-gray-400 flex items-center gap-1"><i className="ri-logout-circle-r-line"></i>{formatDate(p.left_at)}</span>
                                  ) : (
                                    <span className="text-xs text-green-600 flex items-center gap-1"><span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>Présent</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="mt-4 pt-3 border-t border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div><p className="text-xs text-gray-400">Créée le</p><p className="text-xs font-medium text-gray-700">{formatDate(meeting.created_at)}</p></div>
                          <div><p className="text-xs text-gray-400">Terminée le</p><p className="text-xs font-medium text-gray-700">{meeting.ended_at ? formatDate(meeting.ended_at) : '—'}</p></div>
                          <div><p className="text-xs text-gray-400">Durée</p><p className="text-xs font-medium text-gray-700">{formatDuration(meeting.created_at, meeting.ended_at)}</p></div>
                          <div><p className="text-xs text-gray-400">Code</p><p className="text-xs font-mono font-bold text-gray-700">{meeting.code}</p></div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
                  className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                  .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                    if (idx > 0) { const prev = arr[idx - 1]; if (prev !== 'ellipsis' && p - prev > 1) acc.push('ellipsis'); }
                    acc.push(p); return acc;
                  }, [])
                  .map((p, idx) => {
                    if (p === 'ellipsis') return <span key={`e-${idx}`} className="w-9 h-9 flex items-center justify-center text-gray-400">...</span>;
                    return (
                      <button key={p} onClick={() => setPage(p as number)}
                        className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-medium transition-colors cursor-pointer ${page === p ? 'bg-[#00A3E0] text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                        {p}
                      </button>
                    );
                  })}
                <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages}
                  className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </>
        )}

        {/* Contenu Permissions */}
        {activeTab === 'permissions' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Permissions de création</h2>
                <p className="text-xs text-gray-500 mt-0.5">Les utilisateurs listés ici peuvent créer et animer des réunions</p>
              </div>
              <button
                onClick={handleOpenGrantModal}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#00A3E0] text-white rounded-xl hover:bg-[#0088BD] transition-colors font-semibold text-sm whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line"></i>
                Ajouter une permission
              </button>
            </div>

            {permSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2 text-sm text-green-700">
                <i className="ri-check-line"></i>
                {permSuccess}
              </div>
            )}

            {permError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
                <i className="ri-error-warning-line"></i>
                {permError}
              </div>
            )}

            {permLoading ? (
              <div className="text-center py-12">
                <i className="ri-loader-4-line animate-spin text-2xl text-gray-400"></i>
              </div>
            ) : permissions.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-keyhole-line text-2xl text-gray-400"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune permission</h3>
                <p className="text-sm text-gray-500 max-w-sm mx-auto">
                  Aucun utilisateur n'a encore la permission de créer des réunions (hormis les administrateurs).
                </p>
                <button onClick={handleOpenGrantModal} className="mt-4 px-4 py-2 bg-[#00A3E0] text-white rounded-lg hover:bg-[#0088BD] transition-colors text-sm font-medium cursor-pointer">
                  Accorder une permission
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {permissions.map((perm) => {
                  const userName = perm.user
                    ? `${perm.user.raw_user_meta_data?.prenom || ''} ${perm.user.raw_user_meta_data?.nom || ''}`.trim() || perm.user.email
                    : 'Utilisateur inconnu';
                  return (
                    <div key={perm.id} className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-100">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 bg-[#E0F4FC] rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-[#00A3E0]">{userName.charAt(0).toUpperCase()}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
                          <p className="text-xs text-gray-500 truncate">{perm.user?.email || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-gray-400">
                          {formatDate(perm.granted_at)}
                        </span>
                        <button
                          onClick={() => handleRevokePermission(perm.user_id)}
                          className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>
                          Révoquer
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <Footer />

      {/* Modal confirmation terminer */}
      {confirmEndId && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setConfirmEndId(null)}></div>
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Terminer la réunion ?</h3>
            <p className="text-sm text-gray-600 mb-6">
              Tous les participants seront déconnectés. Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmEndId(null)} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm cursor-pointer">Annuler</button>
              <button onClick={() => handleEndMeeting(confirmEndId)} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold text-sm cursor-pointer">
                <i className="ri-stop-circle-line mr-1.5"></i>Terminer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal permission */}
      {showPermModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPermModal(false)}></div>
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Accorder une permission</h3>
            <p className="text-sm text-gray-600 mb-4">
              Entrez l&apos;adresse email de l&apos;utilisateur à qui vous souhaitez donner le droit de créer des réunions.
            </p>
            {permError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
                <i className="ri-error-warning-line"></i>{permError}
              </div>
            )}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Adresse email</label>
              <input
                type="email"
                value={permEmail}
                onChange={(e) => setPermEmail(e.target.value)}
                placeholder="utilisateur@email.com"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#00A3E0]/40 transition-colors"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowPermModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-sm cursor-pointer">Annuler</button>
              <button onClick={handleGrantSubmit} className="flex-1 px-4 py-2.5 bg-[#00A3E0] text-white rounded-xl hover:bg-[#0088BD] transition-colors font-semibold text-sm cursor-pointer">
                <i className="ri-check-line mr-1.5"></i>Accorder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}